import { requestJson } from '../api/http';
import {
  alertPageSchema,
  alertSchema,
  openAlertCountResponseSchema,
  type Alert,
  type AlertPage,
} from './schema';

/** `GET /alerts?status=open&limit=1` - just the open count, for the badge on the note list.
 * The full alert inbox (listing, dismissal) is 6.5's screen; this app only needs the number. */
export function fetchOpenAlertCount(): Promise<number> {
  return requestJson('/alerts?status=open&limit=1', openAlertCountResponseSchema).then(
    (page) => page.open_count,
  );
}

export type AlertStatusFilter = 'open' | 'resolved';

export interface AlertsQuery {
  readonly status?: AlertStatusFilter | undefined;
  readonly limit?: number | undefined;
  readonly cursor?: string | undefined;
}

/** `GET /alerts` - the full paginated inbox (6.5), newest first, plus the open count a badge
 * needs. Defaults to `status=open`, matching the backend's own default. */
export function fetchAlerts(query: AlertsQuery = {}): Promise<AlertPage> {
  const params = new URLSearchParams();
  params.set('status', query.status ?? 'open');
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.cursor !== undefined) {
    params.set('cursor', query.cursor);
  }
  return requestJson(`/alerts?${params.toString()}`, alertPageSchema);
}

/**
 * `POST /alerts/{id}/dismiss` - resolves one alert in place. Dismissing an already-resolved
 * alert is idempotent. Refused with `409 alert_still_blocking` while the reference issue behind
 * it is still open - the caller shows that Spanish message rather than pretending the alert
 * cleared.
 */
export function dismissAlert(alertId: string): Promise<Alert> {
  return requestJson(`/alerts/${alertId}/dismiss`, alertSchema, { method: 'POST' });
}
